import { useCallback, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { Layers } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { DataTable } from '@/components/DataTable'
import type { AdminColumn } from '@/components/DataTable/types'
import { fetchReportGroups, type AdminReportGroup } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'

/**
 * A7.03 — Guruhlash (bir obyektga ko'p shikoyat). JONLI /admin/reports ni obyekt
 * bo'yicha agregatlaydi — eng ko'p shikoyat qilingan foydalanuvchi/guruh/xabar
 * tepada. Alohida endpoint yo'q (klient-agregat).
 */
export function ReportGroups() {
  const t = useT()

  const columns = useMemo<AdminColumn<AdminReportGroup>[]>(
    () => [
      {
        key: 'target_id',
        header: t.mod.target,
        type: 'text',
        minWidth: 180,
        sortable: false,
        render: (r) => {
          const label = `${r.target_type}#${r.target_id}`
          if (r.target_type === 'user')
            return <Link to={`/users/${r.target_id}`} className="text-accent hover:underline">{label}</Link>
          if (r.target_type === 'group' || r.target_type === 'channel')
            return <Link to={`/groups/${r.target_id}`} className="text-accent hover:underline">{label}</Link>
          return <span className="font-mono text-xs">{label}</span>
        },
      },
      {
        key: 'total',
        header: t.mod.reportCount,
        type: 'number',
        width: 110,
        align: 'right',
        render: (r) => <span className="font-mono tabular text-primary">{r.total.toLocaleString()}</span>,
      },
      {
        key: 'pending',
        header: t.mod.pending,
        type: 'number',
        width: 110,
        align: 'right',
        render: (r) => (
          <span className={r.pending > 0 ? 'font-mono tabular text-warning' : 'font-mono tabular text-tertiary'}>
            {r.pending.toLocaleString()}
          </span>
        ),
      },
      { key: 'categories', header: t.mod.category, type: 'text', minWidth: 180, sortable: false },
      { key: 'latest', header: t.mod.latest, type: 'date', width: 160 },
    ],
    [t],
  )

  const fetcher = useCallback(fetchReportGroups, [])

  return (
    <>
      <PageHeader
        title={t.mod.groups}
        screenId="A7.03"
        description={t.mod.groupsNote}
        actions={
          <div className="flex items-center gap-1.5">
            <Link
              to="/moderation/reporters"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              {t.mod.reporterReliability}
            </Link>
            <Link
              to="/moderation"
              className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
            >
              {t.mod.queue}
            </Link>
          </div>
        }
      />
      <DataTable<AdminReportGroup>
        screenId="A7.03"
        permission={PERMISSIONS.USERS_VIEW}
        columns={columns}
        fetcher={fetcher}
        rowId={(r) => `${r.target_type}#${r.target_id}`}
        exportable
        emptyState={{ icon: <Layers size={28} strokeWidth={1.5} /> }}
      />
    </>
  )
}
